export enum Module {
  TERMINAL = 'Terminal',
  ACTIVITIES = 'Activities',
  TIMESHEETS = 'Timesheets',
  VEHICLES = 'Vehicles',
  HANDBOOK = 'Handbook',
  ISSUES = 'Issues',
  FORUM = 'Forum',
  ABSENCES = 'Absences',
  INFRINGEMENTS = 'Infringements',
  WORK_DAY_INFRINGEMENTS = 'WorkInfringements',
  TACHOGRAPH_INFRINGEMENTS = 'TachographInfringements',
  ROUTEPLANNER = 'RoutePlanner',
  FLEET = 'Fleet',
  PLANNER = 'Planner',
  SALARY = 'Salary',
  FORMCREATOR = 'FormCreator',
  USERS = 'Users',
  LEARNING = 'Learning',
  DASHBOARD = 'Dashboard',
  COMPANY_SETTINGS = 'CompanySettings',
  DRIVER_APP = 'DriverApp',
}

export const ALL_MODULES = Object.values(Module)

export const LEADER_GROUP_RESTRICTABLE_MODULES = [
  Module.ACTIVITIES,
  Module.ISSUES,
  Module.FORUM,
  Module.ABSENCES,
  Module.INFRINGEMENTS,
  Module.TIMESHEETS,
  Module.USERS,
] as const

//This "duplicate" enum exists for use in UserExport module field
export enum ModuleName {
  ABSENCES = 'ABSENCES',
  ACTIVITIES = 'ACTIVITIES',
  FLEET = 'FLEET',
  FORMCREATOR = 'FORMCREATOR',
  FORUM = 'FORUM',
  HANDBOOK = 'HANDBOOK',
  INFRINGEMENTS = 'INFRINGEMENTS',
  ISSUES = 'ISSUES',
  ROUTEPLANNER = 'ROUTE_PLANNER',
  SALARY = 'SALARY',
  TERMINAL = 'TERMINAL',
  TIMESHEETS = 'TIMESHEETS',
  VEHICLES = 'VEHICLES',
  USERS = 'USERS',
}

export function isModuleName(value: string): value is ModuleName {
  return Object.values(ModuleName).includes(value as ModuleName)
}

export const SalaryModule = {
  ACTIVITIES: Module.ACTIVITIES,
  TIMESHEETS: Module.TIMESHEETS,
  TERMINAL: Module.TERMINAL,
}

export const ModuleAsPermissionPrefixes = {
  TERMINAL: ['terminal'],
  ACTIVITIES: ['activities', 'dynamic_tables'],
  TIMESHEETS: ['timesheets'],
  VEHICLES: ['vehicles'],
  HANDBOOK: ['handbook', 'formcreator'],
  ISSUES: ['issues'],
  FORUM: ['forum'],
  ABSENCES: ['absences'],
  INFRINGEMENTS: ['infringements'],
  WORK_DAY_INFRINGEMENTS: ['infringements'],
  ROUTEPLANNER: ['route_planner'],
  FLEET: ['fleet'],
  PLANNER: ['planner'],
  SALARY: ['salary'],
  OTHER: ['companies', 'users', 'salaryAddons'],
}

export const Integration = {
  VOLVO: 'Volvo',
  SCANIA: 'Scania',
  FLEETBOARD: 'FleetBoard',
  MAN: 'Man',
}

export enum ModuleOptionsValue {
  ACTIVITIES_RECEIPT_FUNCTIONALITY = 'ACTIVITIES_RECEIPT_FUNCTIONALITY',
  TIMESHEETS_OVERTIME_OPTIONS = 'TIMESHEETS_OVERTIME_OPTIONS',
  TIMESHEETS_COUNT_PAUSE_IN_TOTAL = 'TIMESHEETS_COUNT_PAUSE_IN_TOTAL',
  TIMESHEETS_COMMENT_REQUIRED = 'TIMESHEETS_COMMENT_REQUIRED',
  TIMESHEETS_PROJECTS = 'TIMESHEETS_PROJECTS',
  TIMESHEETS_DAY_INTERVAL_OVERTIME = 'TIMESHEETS_DAY_INTERVAL_OVERTIME',
  MERGE_TIMESHEETS_WITH_ACTIVITIES = 'MERGE_TIMESHEETS_WITH_ACTIVITIES',
  TIMESHEETS_DEPARTMENT_SELECT_REQUIRED = 'TIMESHEETS_DEPARTMENT_SELECT_REQUIRED',
  TIMESHEETS_FACILITY_SELECT_REQUIRED = 'TIMESHEETS_FACILITY_SELECT_REQUIRED',
  TIMESHEETS_DEPARTMENT_FACILITY_FROM_VEHICLE_ONLY = 'TIMESHEETS_DEPARTMENT_FACILITY_FROM_VEHICLE_ONLY',
  CREATE_ISSUE_FROM_INFRINGEMENTS = 'CREATE_ISSUE_FROM_INFRINGEMENTS',
  TIMESHEETS_SALARY_CODE_IN_REGISTRATION = 'TIMESHEETS_SALARY_CODE_IN_REGISTRATION',
  INFRINGEMENTS_USER_CAN_CLOSE_OWN_INFRINGEMENT = 'INFRINGEMENTS_USER_CAN_CLOSE_OWN_INFRINGEMENT',
  ACTIVITIES_SIMPLE_DIET_OPTION = 'ACTIVITIES_SIMPLE_DIET_OPTION',
  ACTIVITIES_DIET_COMMENT_MANDATORY = 'ACTIVITIES_DIET_COMMENT_MANDATORY',
  ACTIVITIES_READ_TACHO_FILES_TO_ACTIVITIES = 'ACTIVITIES_READ_TACHO_FILES_TO_ACTIVITIES',
  ACTIVITIES_PROCESS_TACHO_FILES = 'ACTIVITIES_PROCESS_TACHO_FILES',
  // Summary
  ACTIVITIES_SUMMARY_SHOW_PAUSE = 'ACTIVITIES_SUMMARY_SHOW_PAUSE',
  ACTIVITIES_SUMMARY_SHOW_AVAILABILITY_TIME = 'ACTIVITIES_SUMMARY_SHOW_AVAILABILITY_TIME',
  ACTIVITIES_SUMMARY_SHOW_DRIVING = 'ACTIVITIES_SUMMARY_SHOW_DRIVING',
  ACTIVITIES_SUMMARY_SHOW_OTHER_WORK = 'ACTIVITIES_SUMMARY_SHOW_OTHER_WORK',
  ACTIVITIES_SUMMARY_SHOW_TOTAL_OVERTIME = 'ACTIVITIES_SUMMARY_SHOW_TOTAL_OVERTIME',
  SALARY_ONLY_INCLUDE_APPROVED_HOURS = 'SALARY_ONLY_INCLUDE_APPROVED_HOURS',
  SALARY_USER_APPROVE_HOURS = 'SALARY_USER_APPROVE_HOURS',
  SALARY_LEADER_APPROVE_HOURS = 'SALARY_LEADER_APPROVE_HOURS',
  SALARY_ALLOW_TRANSFER_OF_HOURS_TO_ABSENCES = 'SALARY_ALLOW_TRANSFER_OF_HOURS_TO_ABSENCES',
  SALARY_REQUIRE_COMMENT_ON_DRIVER_APPROVAL = 'SALARY_REQUIRE_COMMENT_ON_DRIVER_APPROVAL',
  SALARY_REQUIRE_COMMENT_ON_MANAGER_APPROVAL = 'SALARY_REQUIRE_COMMENT_ON_MANAGER_APPROVAL',
  SALARY_INCLUDE_UNKNOWN_VEHICLE_ONLY_WORK_PERIODS = 'SALARY_INCLUDE_UNKNOWN_VEHICLE_ONLY_WORK_PERIODS',
  SALARY_GENERATION_RULES = 'SALARY_GENERATION_RULES',
  ACTIVITIES_AUTOMATIC_DRIVER_DIET = 'ACTIVITIES_AUTOMATIC_DRIVER_DIET',
  ACTIVITIES_AUTOMATIC_SALARY_DAYS = 'ACTIVITIES_AUTOMATIC_SALARY_DAYS',
  // Salary approval summary
  SALARY_SUMMARY_SHOW_DRIVING = 'SALARY_SUMMARY_SHOW_DRIVING',
  SALARY_SUMMARY_SHOW_OTHER_WORK = 'SALARY_SUMMARY_SHOW_OTHER_WORK',
  SALARY_SUMMARY_SHOW_AVAILABILITY_TIME = 'SALARY_SUMMARY_SHOW_AVAILABILITY_TIME',
  SALARY_SUMMARY_SHOW_PAUSE = 'SALARY_SUMMARY_SHOW_PAUSE',
  SALARY_SUMMARY_SHOW_PAID_REST = 'SALARY_SUMMARY_SHOW_PAID_REST',
  SALARY_SUMMARY_SHOW_OVERTIME = 'SALARY_SUMMARY_SHOW_OVERTIME',
}
